import { Page } from "shared/ui";
import {
  TaskHubItem,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useDeleteTaskStandAloneAlarmTaskMutation,
  useDeleteTaskStandAloneMutation,
  useGetTasksAlarmStandAloneQuery,
  useGetTasksQuery,
  useGetTasksStandAloneQuery,
  usePatchTaskStandAloneMutation,
  useUpdateTaskMutation,
} from "shared/api/task-hub";
import React, { useRef, useState } from "react";
import {
  useConfirmDeleteModal,
  usePagination,
  useTableSorting,
} from "shared/hooks";
import { useTranslation } from "react-i18next";
import {
  MachineItem,
  useGetAllCompaniesQuery,
  useGetAllMachinesQuery,
  useGetAllStandAloneConfigurationsQuery,
  useGetUsersQuery,
  UserItem,
} from "shared/api";
import { useTaskHubState } from "shared/hooks/useTaskHubState";
import { useTaskHubFilters } from "shared/hooks/useTaskHubFilters";
import { useTaskHubColumns } from "../../../../../shared/hooks/useTaskHubColumns";
import TaskCalendar from "./TaskCalendar";
import { useTaskHubStandAloneSecondTableColumns } from "../../../../../shared/hooks/useTaskHubStandAloneSecondTableColumns";
import { HubSelector } from "./HubSelector";
import { useTaskHubStandAloneColumns } from "../../../../../shared/hooks/useTaskHubStandAloneColumns";
import { TaskHubStandalone } from "./TaskHubStandalone";
import { TaskHubMain } from "./TaskHubMain";

export enum TaskHubType {
  TASK_HUB = "Task Hub - Human",
  STAND_ALONE = "Stand Alone",
}

const TaskHub = () => {
  const { t } = useTranslation();
  const pagination = usePagination(10);
  const [selectedHub, setSelectedHub] = useState<TaskHubType>(
    TaskHubType.TASK_HUB
  );

  const state = useTaskHubState();
  const filters = useTaskHubFilters();

  const { setCurrentPage, pageSize, currentPage, offset, limit } = pagination;
  const { ordering } = useTableSorting();

  const { data: allUsers = [] } = useGetUsersQuery({}) as { data?: UserItem[] };
  const transformedUsers = allUsers?.map((user) => ({
    label: user.username,
    value: user.id,
  }));

  const taskScheduleRef = useRef<{ refetchExecutions: () => void }>(null);

  const extractIds = (arr: any[], key: string) =>
    arr?.map((item) => item[key]?.id ?? item.id) || [];

  const { data: machinesData } = useGetAllMachinesQuery({}) as {
    data: MachineItem[] | undefined;
  };

  const { data: serversResp } = useGetAllStandAloneConfigurationsQuery();
  const serversOptions =
    serversResp?.map((s) => ({ label: s.name, value: s.id })) ?? [];

  const { data, isLoading, refetch } = useGetTasksQuery({
    offset,
    limit,
    ordering,
    name__icontains: filters.debouncedTaskNameValue,
    periodicity: filters.debouncedPeriodicityValue,
    start_periodicity__gte: filters.startDateFilters.dateGreater ?? undefined,
    start_periodicity__lte: filters.startDateFilters.dateLesser ?? undefined,
    priority__gte: filters.priorityFilters.minValue ?? undefined,
    priority__lte: filters.priorityFilters.maxValue ?? undefined,
    enabled: filters.enabledFilter,
    users__user__in: filters.userIds.length > 0 ? filters.userIds : undefined,
    trigger__in:
      filters.triggerInFilter.length > 0 ? filters.triggerInFilter : undefined,
    expand: "machines,product_cards,files,users,companies",
  });

  const {
    data: standAloneData,
    isLoading: loadingStandAlone,
    refetch: standAloneRefetch,
  } = useGetTasksStandAloneQuery({
    offset,
    limit,
    name__icontains: filters.debouncedStandAloneNameValue || undefined,
    influxdb_server: filters.serverFilter,
    field__icontains: filters.fieldValue || undefined,
    measurement__icontains: filters.measurementValue || undefined,
    lookback_days__gte: filters.lookbackRange.minValue ?? undefined,
    lookback_days__lte: filters.lookbackRange.maxValue ?? undefined,
    priority__gte: filters.priorityRange.minValue ?? undefined,
    priority__lte: filters.priorityRange.maxValue ?? undefined,
    target_value__gte: filters.targetRange.minValue ?? undefined,
    target_value__lte: filters.targetRange.maxValue ?? undefined,
    min_soft__gte: filters.minSoftRange.minValue ?? undefined,
    min_soft__lte: filters.minSoftRange.maxValue ?? undefined,
    max_soft__gte: filters.maxSoftRange.minValue ?? undefined,
    max_soft__lte: filters.maxSoftRange.maxValue ?? undefined,
    min_hard__gte: filters.minHardRange.minValue ?? undefined,
    min_hard__lte: filters.minHardRange.maxValue ?? undefined,
    max_hard__gte: filters.maxHardRange.minValue ?? undefined,
    max_hard__lte: filters.maxHardRange.maxValue ?? undefined,
    last_timestamp_with_data__gte:
      filters.lastTimestampFilters.dateGreater ?? undefined,
    last_timestamp_with_data__lte:
      filters.lastTimestampFilters.dateLesser ?? undefined,
    enabled: filters.enabledFilter ?? undefined,
  });

  const taskStandaloneOptions =
    standAloneData?.results?.map((t) => ({ label: t.name, value: t.id })) ?? [];

  const { data: suppliersCompanies } = useGetAllCompaniesQuery({
    is_supplier: true,
  });

  const suppliersCompaniesFormatted = suppliersCompanies?.map((s) => ({
    label: s.name,
    value: s.id,
  }));

  const { data: tasksAlarmStandAlone, refetch: tasksAlarmStandAloneRefetch } =
    useGetTasksAlarmStandAloneQuery({
      expand: "task_standalone",
      is_active: filters.isActiveFilter,
      task_standalone__in:
        filters.taskStandaloneFilter?.length > 0
          ? filters.taskStandaloneFilter.join(",")
          : undefined,
      timestamp_created__gte: filters.createdFilters.dateGreater ?? undefined,
      timestamp_created__lte: filters.createdFilters.dateLesser ?? undefined,
      timestamp_updated__gte: filters.updatedFilters.dateGreater ?? undefined,
      timestamp_updated__lte: filters.updatedFilters.dateLesser ?? undefined,
      is_min_soft: filters.isMinSoftFilter ?? undefined,
      is_max_soft: filters.isMaxSoftFilter ?? undefined,
      is_min_hard: filters.isMinHardFilter ?? undefined,
      is_max_hard: filters.isMaxHardFilter ?? undefined,
      max_value__gte: filters.maxValueGte ?? undefined,
      max_value__lte: filters.maxValueLte ?? undefined,
      min_value__gte: filters.minValueGte ?? undefined,
      min_value__lte: filters.minValueLte ?? undefined,
      timestamp_triggered__gte:
        filters.triggeredFilters.dateGreater ?? undefined,
      timestamp_triggered__lte:
        filters.triggeredFilters.dateLesser ?? undefined,
      comments__icontains: filters.debouncedCommentsValue || undefined,
    });

  const [deleteTask] = useDeleteTaskMutation();
  const [deleteTaskStandAlone] = useDeleteTaskStandAloneMutation();
  const [deleteTaskStandAloneAlarm] =
    useDeleteTaskStandAloneAlarmTaskMutation();
  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [patchTaskStandAlone] = usePatchTaskStandAloneMutation();

  const handleDuplicate = async (task: TaskHubItem) => {
    try {
      const duplicated = {
        ...Object.fromEntries(
          Object.entries(task).filter(
            ([key]) =>
              key !== "icon" && key !== "time" && key !== "end_periodicity"
          )
        ),
        name: `${task.name} (copy)`,
        machines: extractIds(task.machines, "machine"),
        product_cards: extractIds(task.product_cards, "product_card"),
        users: extractIds(task.users, "user"),
        companies: extractIds(task.companies, "companies"),
      };

      await createTask(duplicated).unwrap();
      refetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to duplicate task", err);
    }
  };

  const deletTaskHandler = async (id: number) => {
    try {
      await deleteTask(id).unwrap();
      refetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to delete task", err);
    }
  };

  const handleDeleteStandAlone = async (id: number) => {
    try {
      await deleteTaskStandAlone(id).unwrap();
    } catch (e) {
      console.error("Delete failed", e);
    }
  };

  const handleDeleteStandAloneAlarm = async (id: number) => {
    try {
      await deleteTaskStandAloneAlarm(id).unwrap();
    } catch (e) {
      /* eslint-disable no-console */
      console.error("Delete failed", e);
    }
  };

  const handleToggleStatus = async (task: TaskHubItem) => {
    try {
      await updateTask({
        id: task.id,
        name: task.name,
        periodicity: task.periodicity,
        start_periodicity: task.start_periodicity,
        priority: task.priority,
        enabled: !task.enabled,
        users: extractIds(task.users, "user"),
        user__full_name: task.user__full_name,
        description: task.description,
        machines: extractIds(task.machines, "machine"),
        product_cards: extractIds(task.product_cards, "product_card"),
      }).unwrap();
      refetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to toggle status", err);
    }
  };

  const handleToggleStatusStandAlone = async (task: any) => {
    try {
      await patchTaskStandAlone({
        id: task.id,
        enabled: !task.enabled,
      }).unwrap();
      standAloneRefetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to toggle standalone task status", err);
    }
  };

  const confirmDelete = useConfirmDeleteModal(
    t("Are you sure you want to delete this task?"),
    (record: number) => deletTaskHandler(record)
  );

  const confirmDeleteStandAlone = useConfirmDeleteModal(
    t("Are you sure you want to delete this standalone task?"),
    (record: number) => handleDeleteStandAlone(record)
  );

  const confirmDeleteStandAloneAlarmTask = useConfirmDeleteModal(
    t("Are you sure you want to delete this alarm task?"),
    (record: number) => handleDeleteStandAloneAlarm(record)
  );

  const columns = useTaskHubColumns({
    onEdit: (task: number | null) => {
      state.setEditingTask(task);
      state.setIsEditModalOpen(true);
    },
    onDelete: (id: number | null) => {
      if (id !== null) confirmDelete(id);
    },
    onDuplicate: handleDuplicate,
    transformedUsers,
    handleToggleStatus,
    handleTaskNameValue: filters.handleTaskNameValue,
    taskNameValue: filters.taskNameValue,
    handlePeriodicityValue: filters.handlePeriodicityValue,
    periodicityValue: filters.periodicityValue,
    enabledFilter: filters.enabledFilter,
    setEnabledFilter: filters.setEnabledFilter,
    handleStartDateGreater: filters.startDateFilters.handleDateGreater,
    handleStartDateLesser: filters.startDateFilters.handleDateLesser,
    startDateGreater: filters.startDateFilters.dateGreater,
    startDateLesser: filters.startDateFilters.dateLesser,
    isStartFilterActive: filters.startDateFilters.isDataFilterActive,
    userIds: filters.userIds,
    priorityFilters: filters.priorityFilters,
    setUserIds: filters.setUserIds,
    triggerInFilter: filters.triggerInFilter,
    setTriggerInFilter: filters.setTriggerInFilter,
    refetch,
  });

  const columnsStandAlone = useTaskHubStandAloneColumns({
    filters,
    serversOptions,
    onEdit: (id) => {
      state.setEditingStandAloneId(id);
      state.setIsEditStandAloneOpen(true);
    },
    onDelete: (id) => confirmDeleteStandAlone(id),
    onToggleStatus: handleToggleStatusStandAlone,
  });

  const columnsStandAloneSecondTable = useTaskHubStandAloneSecondTableColumns({
    isActiveFilter: filters.isActiveFilter,
    setIsActiveFilter: filters.setIsActiveFilter,
    taskStandaloneFilter: filters.taskStandaloneFilter,
    setTaskStandaloneFilter: filters.setTaskStandaloneFilter,
    taskStandaloneOptions,
    refetch: tasksAlarmStandAloneRefetch,
    onEdit: (record) => {
      state.setEditingAlarm(record);
      state.setIsEditAlarmOpen(true);
    },
    onDelete: (id) => confirmDeleteStandAloneAlarmTask(id),
    createdDateGreater: filters.createdFilters.dateGreater,
    createdDateLesser: filters.createdFilters.dateLesser,
    handleCreatedDateGreater: filters.createdFilters.handleDateGreater,
    handleCreatedDateLesser: filters.createdFilters.handleDateLesser,
    isCreatedFilterActive: filters.createdFilters.isDataFilterActive,
    updatedDateGreater: filters.updatedFilters.dateGreater,
    updatedDateLesser: filters.updatedFilters.dateLesser,
    handleUpdatedDateGreater: filters.updatedFilters.handleDateGreater,
    handleUpdatedDateLesser: filters.updatedFilters.handleDateLesser,
    isUpdatedFilterActive: filters.updatedFilters.isDataFilterActive,
    maxValueGte: filters.maxValueGte,
    setMaxValueGte: filters.setMaxValueGte,
    maxValueLte: filters.maxValueLte,
    setMaxValueLte: filters.setMaxValueLte,
    minValueGte: filters.minValueGte,
    setMinValueGte: filters.setMinValueGte,
    minValueLte: filters.minValueLte,
    setMinValueLte: filters.setMinValueLte,
    triggeredDateGreater: filters.triggeredFilters.dateGreater,
    triggeredDateLesser: filters.triggeredFilters.dateLesser,
    handleTriggeredDateGreater: filters.triggeredFilters.handleDateGreater,
    handleTriggeredDateLesser: filters.triggeredFilters.handleDateLesser,
    isTriggeredFilterActive: filters.triggeredFilters.isDataFilterActive,
    commentsValue: filters.commentsValue,
    handleCommentsValue: filters.handleCommentsValue,
  });

  return state.isCalendarOpen ? (
    <>
      {selectedHub === TaskHubType.TASK_HUB && (
        <TaskCalendar
          mode="executions"
          onBackClick={() => state.setIsCalendarOpen(false)}
        />
      )}

      {selectedHub === TaskHubType.STAND_ALONE && (
        <TaskCalendar
          mode="standalone-alarms"
          onBackClick={() => state.setIsCalendarOpen(false)}
        />
      )}
    </>
  ) : (
    <Page
      title={t("taskHub.taskHub")}
      buttonTitle={t("taskHub.buttons.addTask") || ""}
      onClick={() => {
        if (selectedHub === TaskHubType.TASK_HUB) {
          state.setIsAddModalOpen(true);
        }
        if (selectedHub === TaskHubType.STAND_ALONE) {
          state.setIsAddModalStandAloneOpen(true);
        }
      }}
      isVisibleClearFiltersButton={filters.isVisibleClearFiltersButton}
      clearAllFilters={filters.clearAllFilters}
      isCalendar
      calendarTitle={t("taskHub.buttons.tasksSchedule") || ""}
      onCalendarClick={() => state.setIsCalendarOpen(true)}
      titleChildren={
        <HubSelector
          options={[
            {
              label: TaskHubType.TASK_HUB,
              value: TaskHubType.TASK_HUB,
              img: "/task-hub-icon-1.webp",
              alt: "Task Hub - Human",
            },
            {
              label: TaskHubType.STAND_ALONE,
              value: TaskHubType.STAND_ALONE,
              img: "/task-hub-icon-2.jpg",
              alt: "Stand Alone",
            },
          ]}
          value={selectedHub}
          onChange={(v) => setSelectedHub(v)}
        />
      }
    >
      {selectedHub === TaskHubType.TASK_HUB ? (
        <TaskHubMain
          data={data}
          isLoading={isLoading}
          currentPage={currentPage}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          setPageSize={pagination.setPageSize!}
          columns={columns}
          suppliersCompaniesFormatted={suppliersCompaniesFormatted}
          machinesData={machinesData}
          transformedUsers={transformedUsers}
          modalState={{ ...state }}
          refetch={refetch}
          taskScheduleRef={taskScheduleRef}
        />
      ) : (
        <TaskHubStandalone
          standAloneData={standAloneData}
          loadingStandAlone={loadingStandAlone}
          currentPage={currentPage}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          setPageSize={pagination.setPageSize!}
          columnsStandAlone={columnsStandAlone}
          tasksAlarmStandAlone={tasksAlarmStandAlone}
          columnsStandAloneSecondTable={columnsStandAloneSecondTable}
          tasksAlarmStandAloneRefetch={tasksAlarmStandAloneRefetch}
          modalState={{ ...state }}
        />
      )}
    </Page>
  );
};

export { TaskHub };
